import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/authSlice";
import { useHasAdminAccess } from "../../hooks/usePermissions";
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  MobileNavHeader,
  MobileNavMenu,
  MobileNavToggle,
  NavbarLogo,
  NavbarButton,
} from "../ui/resizable-navbar";
import { IconChevronDown } from "@tabler/icons-react";
import { AnimatePresence, motion } from "motion/react";

const aboutChildren = [
  { name: "Документы", link: "/documents" },
  { name: "Об организации", link: "/about/organization" },
  { name: "Контакты", link: "/about/contacts" },
  { name: "Наша команда", link: "/about/team" },
];

const MobileSublist = ({ item, onClose }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-md px-2 py-2 text-neutral-700 dark:text-neutral-200"
      >
        <span>{item.name}</span>
        <IconChevronDown
          className={"h-4 w-4 transition-transform " + (open ? "rotate-180" : "")}
        />
      </button>
      {open && (
        <div className="ml-3 flex flex-col gap-1 border-l border-neutral-200 pl-3 dark:border-neutral-800">
          {item.children.map((child, i) => (
            <NavLink
              key={child.link}
              to={child.link}
              onClick={onClose}
              className="rounded-md px-2 py-2 text-sm text-neutral-600 dark:text-neutral-300"
            >
              {child.name}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const Menu = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [joinRedirect, setJoinRedirect] = useState("/login");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useSelector((state) => ({
    isAuthenticated: state.auth.isAuthenticated,
  }));
  const isAdmin = useHasAdminAccess();

  const actionRef = useRef(null);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    const handler = (e) => {
      if (actionRef.current && !actionRef.current.contains(e.target)) {
        setActionOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    try {
      const response = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        dispatch(logout());
        navigate("/login");
      } else {
        console.error("Error during logout");
      }
    } catch (error) {
      console.error("Network error during logout attempt:", error);
    }
  };

  const handleJoinClick = () => {
    setJoinRedirect(isAuthenticated ? "/profile" : "/login");
    setShowJoinModal(true);
  };

  const navItems = [
    { name: "Главная", link: "/" },
    { name: "О нас", children: aboutChildren },
  ];
  if (isAuthenticated) {
    navItems.push({ name: "Личный кабинет", link: "/profile" });
  }
  if (isAdmin) {
    navItems.push({ name: "Админка", link: "/admin" });
  }

  const isHome = location.pathname === "/";

  return (
    <>
      <Navbar className={isHome ? "fixed top-4 z-50" : "sticky top-0 z-50"}>
        <NavBody>
          <NavbarLogo />
          <NavItems items={navItems} onItemClick={() => setMobileOpen(false)} />

          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <NavbarButton variant="secondary" onClick={handleLogout}>
                Выйти
              </NavbarButton>
            ) : (
              <NavbarButton as={NavLink} to="/login" variant="secondary">
                Войти
              </NavbarButton>
            )}

            <div className="relative" ref={actionRef}>
              <NavbarButton
                variant="primary"
                onClick={() => setActionOpen((o) => !o)}
                className="flex items-center gap-1"
              >
                Действуй!
                <IconChevronDown className="h-4 w-4" />
              </NavbarButton>
              <AnimatePresence>
                {actionOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-60 rounded-xl border border-black/10 bg-white p-2 shadow-xl dark:bg-neutral-950 z-[70]"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setActionOpen(false);
                        handleJoinClick();
                      }}
                      className="block w-full rounded-md px-3 py-2 text-left text-sm text-neutral-700 hover:bg-gray-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
                    >
                      Вступить в организацию
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActionOpen(false);
                        setShowDonateModal(true);
                      }}
                      className="block w-full rounded-md px-3 py-2 text-left text-sm text-neutral-700 hover:bg-gray-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
                    >
                      Поддержать
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </NavBody>

        <MobileNav>
          <MobileNavHeader>
            <NavbarLogo />
            <MobileNavToggle
              isOpen={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
            />
          </MobileNavHeader>
          <MobileNavMenu isOpen={mobileOpen} onClose={() => setMobileOpen(false)}>
            <NavLink
              to="/"
              onClick={() => setMobileOpen(false)}
              className="w-full rounded-md px-2 py-2 text-neutral-700 dark:text-neutral-200"
            >
              Главная
            </NavLink>

            <MobileSublist
              item={{ name: "О нас", children: aboutChildren }}
              onClose={() => setMobileOpen(false)}
            />

            {isAuthenticated && (
              <NavLink
                to="/profile"
                onClick={() => setMobileOpen(false)}
                className="w-full rounded-md px-2 py-2 text-neutral-700 dark:text-neutral-200"
              >
                Личный кабинет
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/admin"
                onClick={() => setMobileOpen(false)}
                className="w-full rounded-md px-2 py-2 text-neutral-700 dark:text-neutral-200"
              >
                Админка
              </NavLink>
            )}

            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                handleJoinClick();
              }}
              className="w-full rounded-md px-2 py-2 text-left text-neutral-700 dark:text-neutral-200"
            >
              Вступить в организацию
            </button>
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                setShowDonateModal(true);
              }}
              className="w-full rounded-md px-2 py-2 text-left text-neutral-700 dark:text-neutral-200"
            >
              Поддержать
            </button>

            {isAuthenticated ? (
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  handleLogout();
                }}
                className="w-full rounded-md px-2 py-2 text-left text-neutral-700 dark:text-neutral-200"
              >
                Выйти
              </button>
            ) : (
              <NavLink
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="w-full rounded-md px-2 py-2 text-neutral-700 dark:text-neutral-200"
              >
                Войти
              </NavLink>
            )}
          </MobileNavMenu>
        </MobileNav>
      </Navbar>

      {showJoinModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-neutral-900">
            <p className="mb-4 text-neutral-800 dark:text-neutral-100">
              {isAuthenticated
                ? "Перед тем, как подать заявление, убедитесь, что все поля заполнены верно."
                : "Чтобы подать заявление о вступлении, нужно войти на сайт и заполнить информацию в личном кабинете."}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowJoinModal(false)}
                className="rounded-md px-4 py-2 text-neutral-600 hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowJoinModal(false);
                  window.location.href = joinRedirect;
                }}
                className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
              >
                Войти
              </button>
            </div>
          </div>
        </div>
      )}

      {showDonateModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl dark:bg-neutral-900">
            <p className="mb-4 text-neutral-800 dark:text-neutral-100">
              Отсканируйте для пожертвования
            </p>
            <img
              src="/images/qr-lksm.jpg"
              alt="QR-код для пожертвований"
              className="mx-auto mb-4 h-48 w-48 object-contain"
            />
            <button
              type="button"
              onClick={() => setShowDonateModal(false)}
              className="rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Menu;
